/**
 * Monaco Formula Editor Component
 * 
 * A schema-aware formula editor using Monaco Editor with:
 * - Custom language registration ("formula-lang")
 * - Intelligent completion provider (columns vs operators)
 * - Syntax highlighting
 * 
 * Note: Validation is performed on the backend when saving/executing formulas.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';

import { fetchOperators, getAllOperators, type OperatorDefinition, type SchemaColumn } from './operatorConstants';
import { registerFormulaSignatureHelp } from './signatureHelpProvider';
import { updateColumnDecorations } from './columnDecorations';
import { validateFormula, removeStringLiterals } from '../utils/formulaHelpers';

import { useUIStore } from '@/store/useUI';
import { logger } from '@/lib/logger';
import type { LinkedTableVariable } from '@/lib/types';

// Import Monaco overrides (column reference decorations, etc.)
import './monaco-overrides.css';

export interface FormulaEditorHandle {
  /** Insert text at the current cursor position (preserves undo history) */
  insertText: (text: string) => void;
  /** Focus the editor */
  focus: () => void;
}

interface MonacoFormulaEditorProps {
  /** Current formula value */
  value: string;
  /** Callback when formula changes */
  onChange: (value: string) => void;
  /** Available schema columns */
  schema: SchemaColumn[];
  /** Available variables (suggested after $) */
  variables?: LinkedTableVariable[];
  /** Editor height */
  height?: string;
  /** Compact mode - reduced padding for inline usage */
  compact?: boolean;
  /** Debounce delay for validation (ms) */
  debounceMs?: number;
}

/**
 * Monaco Formula Editor Component
 */
export const MonacoFormulaEditor = forwardRef<FormulaEditorHandle, MonacoFormulaEditorProps>(
  function MonacoFormulaEditor({
    value,
    onChange,
    schema,
    variables = [],
    height = '100px',
    compact = false,
    debounceMs = 300,
  }, ref) {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof monaco | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [operators, setOperators] = useState<OperatorDefinition[]>([]);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const completionProviderRef = useRef<monaco.IDisposable | null>(null);
  const signatureHelpRef = useRef<monaco.IDisposable | null>(null);
  const decorationIdsRef = useRef<string[]>([]);

  // Expose imperative handle for inserting text from parent
  useImperativeHandle(ref, () => ({
    insertText(text: string) {
      const editor = editorRef.current;
      if (!editor) return;
      const position = editor.getPosition();
      if (!position) return;

      // Insert text at cursor, preserving undo history
      editor.executeEdits('formula-insert', [{
        range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
        text,
      }]);

      // Move cursor inside parentheses if text ends with ()
      if (text.endsWith('()')) {
        const newCol = position.column + text.length - 1;
        editor.setPosition({ lineNumber: position.lineNumber, column: newCol });
      }
      editor.focus();
    },
    focus() {
      editorRef.current?.focus();
    },
  }));
  
  // Get theme from Zustand store
  const theme = useUIStore((state) => state.theme);
  
  // Resolve theme (handle 'system' preference)
  const resolvedTheme = theme === 'system' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;

  // Fetch operators on mount
  useEffect(() => {
    fetchOperators()
      .then(() => {
        setOperators(getAllOperators());
      })
      .catch((error) => {
        logger.debug('Failed to load operators for Monaco editor:', error);
      });
  }, []);

  // Re-register syntax highlighting and signature help when operators change
  useEffect(() => {
    if (monacoRef.current && isReady) {
      registerFormulaLanguage(monacoRef.current, operators);

      // Dispose and re-register signature help provider
      if (signatureHelpRef.current) {
        signatureHelpRef.current.dispose();
      }
      signatureHelpRef.current = registerFormulaSignatureHelp(monacoRef.current, operators);
    }

    return () => {
      if (signatureHelpRef.current) {
        signatureHelpRef.current.dispose();
        signatureHelpRef.current = null;
      }
    };
  }, [operators, isReady]);

  // Re-register completion provider when operators, schema, or variables change
  useEffect(() => {
    if (monacoRef.current && isReady) {
      // Dispose previous provider
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
      }

      // Register with loaded operators (even if empty, to avoid issues)
      // Pass the editor's model so the provider only fires for this specific editor
      completionProviderRef.current = registerCompletionProvider(
        monacoRef.current,
        schema,
        operators,
        variables,
        editorRef.current?.getModel()
      );
    }

    // Cleanup: dispose completion provider when component unmounts or dependencies change
    return () => {
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
        completionProviderRef.current = null;
      }
    };
  }, [operators, schema, variables, isReady]);

  /**
   * Handle editor mount
   */
  const handleEditorDidMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoRef.current = monacoInstance;

    // Register custom language
    registerFormulaLanguage(monacoInstance, operators);

    // Note: Completion provider is registered in useEffect when operators are loaded
    // This prevents duplicate registrations

    // Set initial validation and decorations
    validateAndShowDiagnostics(value, monacoInstance, editor);
    decorationIdsRef.current = updateColumnDecorations(editor, monacoInstance, decorationIdsRef.current);

    setIsReady(true);
  };

  /**
   * Handle editor change
   */
  const handleEditorChange = (newValue: string | undefined) => {
    const formulaText = newValue ?? '';
    onChange(formulaText);

    // Debounced validation
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      if (monacoRef.current && editorRef.current) {
        validateAndShowDiagnostics(formulaText, monacoRef.current, editorRef.current);
        decorationIdsRef.current = updateColumnDecorations(editorRef.current, monacoRef.current, decorationIdsRef.current);
      }
    }, debounceMs);
  };

  /**
   * Validate formula and show diagnostics in Monaco editor
   */
  const validateAndShowDiagnostics = (
    text: string,
    monacoInstance: typeof monaco,
    editor: monaco.editor.IStandaloneCodeEditor
  ) => {
    const model = editor.getModel();
    if (!model) return;

    // Convert schema to validation format
    const columnsForValidation = schema.map((col) => ({
      id: col.id,
      name: col.label || col.id,
      table_alias: col.tableAlias,
    }));

    // Build valid function names from dynamically loaded operators
    const validFunctionNames = new Set(
      operators.filter((op) => /^[A-Z]/.test(op.name)).map((op) => op.name)
    );

    // Run validation with dynamic operator list
    const errors = validateFormula(text, columnsForValidation, validFunctionNames.size > 0 ? validFunctionNames : undefined);

    // Convert FormulaError[] to Monaco markers
    const markers: monaco.editor.IMarkerData[] = errors.map((error) => {
      // Try to find specific position for the error
      let startColumn = 1;
      let endColumn = model.getLineMaxColumn(1);

      if (error.position !== undefined && error.position >= 0) {
        // Use position from the error directly
        startColumn = error.position + 1; // Monaco is 1-based
        // Find the end of the problematic token
        if (error.type === 'operator') {
          const funcMatch = text.slice(error.position).match(/^[A-Z][A-Z0-9]*/);
          endColumn = startColumn + (funcMatch ? funcMatch[0].length : 1);
        } else {
          endColumn = startColumn + 2; // Default small highlight
        }
      } else if (error.type === 'reference') {
        // Highlight the specific [column] reference
        const refMatch = error.message.match(/\[([^\]]+)\]/);
        if (refMatch) {
          const refIndex = text.indexOf(refMatch[0]);
          if (refIndex !== -1) {
            startColumn = refIndex + 1;
            endColumn = refIndex + refMatch[0].length + 1;
          }
        }
      }

      return {
        severity: monacoInstance.MarkerSeverity.Error,
        message: error.message,
        source: 'Formula',
        startLineNumber: 1,
        startColumn,
        endLineNumber: 1,
        endColumn,
      };
    });

    monacoInstance.editor.setModelMarkers(model, 'formula-validator', markers);
  };

  /**
   * Update validation when schema changes
   */
  useEffect(() => {
    if (isReady && monacoRef.current && editorRef.current) {
      validateAndShowDiagnostics(value, monacoRef.current, editorRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, isReady]);

  /**
   * Cleanup
   */
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full overflow-visible">
      <div className="relative w-full h-full border border-border rounded-lg overflow-visible bg-background font-mono text-sm md:text-base shadow-sm">
        <Editor
          height={height}
          language="formula-lang"
          value={value}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme={resolvedTheme === 'dark' ? 'vs-dark' : 'vs-light'}
          options={{
            minimap: { enabled: false },
            lineNumbers: 'off',
            glyphMargin: false,
            folding: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
            scrollBeyondLastLine: false,
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
            wordWrap: 'on',
            wrappingStrategy: 'advanced',
            fontSize: 14,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            padding: compact ? { top: 4, bottom: 4 } : { top: 8, bottom: 8 },
            suggest: {
              showWords: false,
              showSnippets: false,
              insertMode: 'replace',
            },
            quickSuggestions: {
              other: true,
              comments: false,
              strings: false,
            },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
            automaticLayout: true,
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            renderLineHighlight: 'none',
            contextmenu: false,
            fixedOverflowWidgets: true,
          }}
        />
      </div>
    </div>
  );
});

/**
 * Register custom formula language
 */
/** Build a regex alternation pattern from uppercase function operator names */
function buildFunctionNamePattern(operators: OperatorDefinition[]): string {
  return operators.filter(op => /^[A-Z]/.test(op.name)).map(op => op.name).join('|') || 'NEVER_MATCH';
}

function registerFormulaLanguage(monacoInstance: typeof monaco, operators: OperatorDefinition[]) {
  const languages = monacoInstance.languages.getLanguages();
  const isRegistered = languages.some((lang) => lang.id === 'formula-lang');

  // Register language if not already registered
  if (!isRegistered) {
    monacoInstance.languages.register({ id: 'formula-lang' });
  }

  // Set language configuration
  monacoInstance.languages.setLanguageConfiguration('formula-lang', {
    brackets: [
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
  });

  // Set token provider for syntax highlighting
  monacoInstance.languages.setMonarchTokensProvider('formula-lang', {
    tokenizer: {
      root: [
        // Column references
        [/\[[^\]]+\]/, 'variable'],

        // Variable references ($name)
        [/\$[a-zA-Z_][a-zA-Z0-9_]*/, 'variable.predefined'],

        // String literals
        [/"[^"]*"/, 'string'],

        // Number literals
        [/\d+\.?\d*/, 'number'],
        
        // Function names (dynamically built from loaded operators)
        [new RegExp(`\\b(${buildFunctionNamePattern(operators)})\\b`), 'keyword'],
        
        // Operators
        [/==|!=|>=|<=|>|<|\+|-|\*|\/|%|\^|&/, 'operator'],
        
        // Punctuation
        [/[(),]/, 'delimiter'],
      ],
    },
  });
}

/**
 * Register completion provider
 */
function registerCompletionProvider(
  monacoInstance: typeof monaco,
  schema: SchemaColumn[],
  operators: OperatorDefinition[],
  variables: LinkedTableVariable[],
  targetModel?: monaco.editor.ITextModel | null
): monaco.IDisposable {
  logger.debug('Registering completion provider', { schemaFieldCount: schema.length, variableCount: variables.length });

  // Register and return the disposable
  return monacoInstance.languages.registerCompletionItemProvider('formula-lang', {
    triggerCharacters: ['[', '$', ' ', '=', '>', '<', '!'],

    provideCompletionItems: (model, position) => {
      // Only provide suggestions for this specific editor's model
      if (targetModel && model !== targetModel) {
        return { suggestions: [] };
      }

      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      // Get the current word range — used so Monaco replaces typed text on accept
      const word = model.getWordUntilPosition(position);
      const wordRange = {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };

      // Determine context: are we in a column reference?
      const inColumnContext = isInColumnContext(textUntilPosition);

      // Check if we're in a variable context (cursor after $)
      const variableContext = getVariableContext(textUntilPosition);

      if (inColumnContext) {
        logger.debug('Providing column suggestions', { schemaFieldCount: schema.length });
        // Provide column suggestions
        return {
          suggestions: schema.map((col) => {
            const fieldLabel = col.label || col.id;
            // Check if field is already prefixed (relationship fields come pre-prefixed from backend)
            const isAlreadyPrefixed = fieldLabel.includes('.');

            const displayName = isAlreadyPrefixed
              ? fieldLabel // Already prefixed, use as-is
              : col.tableAlias
              ? `${col.tableAlias}.${fieldLabel}` // Add table alias prefix
              : fieldLabel; // No prefix needed

            return {
              label: displayName,
              kind: monacoInstance.languages.CompletionItemKind.Field,
              detail: col.description || `${col.data_type || 'unknown'} field`,
              documentation: col.description,
              insertText: displayName,
              range: wordRange,
            };
          }),
        };
      } else if (variableContext && variables.length > 0) {
        // Provide variable suggestions — replace from $ to cursor
        const variableRange = {
          startLineNumber: position.lineNumber,
          startColumn: variableContext.startColumn,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };

        return {
          suggestions: variables
            .filter(v => v.name) // Only suggest named variables
            .map((v) => ({
              label: `$${v.name}`,
              kind: monacoInstance.languages.CompletionItemKind.Variable,
              detail: `${v.type} variable`,
              documentation: {
                value: `**Variable:** \`$${v.name}\`\n\n**Type:** ${v.type}\n\n**Default:** ${v.default_value || '(empty)'}`,
                isTrusted: true,
              },
              insertText: `$${v.name}`,
              range: variableRange,
            })),
        };
      } else {
        // Provide function suggestions (filter out symbolic operators like +, -, *, etc.)
        return {
          suggestions: operators
            .filter((op) => /^[A-Z]/.test(op.name))
            .map((op: OperatorDefinition) => ({
              label: op.name,
              kind: monacoInstance.languages.CompletionItemKind.Function,
              detail: op.label,
              documentation: {
                value: `**${op.label}**\n\n${op.description}\n\n*Example:* \`${op.example}\``,
                isTrusted: true,
              },
              insertText: op.name,
              range: wordRange,
            })),
        };
      }
    },
  });
}

/**
 * Determine if cursor is in a column context (inside [])
 * Excludes brackets that appear inside string literals
 */
function isInColumnContext(textUntilCursor: string): boolean {
  // Remove string literals to avoid counting brackets inside them
  // e.g., IF([status] = "[pending]", 1, 0) - don't count "[pending]" brackets
  const textWithoutStrings = removeStringLiterals(textUntilCursor);

  // Count opening and closing brackets in the cleaned text
  const openBrackets = (textWithoutStrings.match(/\[/g) || []).length;
  const closeBrackets = (textWithoutStrings.match(/\]/g) || []).length;

  // If more opening than closing, we're inside a column reference
  return openBrackets > closeBrackets;
}

/**
 * Detect if cursor is in a variable context (after $)
 * Returns the column position of the $ character (1-based) for replacement range,
 * or null if not in a variable context.
 */
function getVariableContext(
  textUntilCursor: string
): { startColumn: number } | null {
  // Match $ followed by optional identifier chars at the end of text
  const match = textUntilCursor.match(/\$([a-zA-Z_][a-zA-Z0-9_]*)?$/);
  if (!match) return null;

  // startColumn is 1-based, pointing at the $
  const startColumn = textUntilCursor.length - match[0].length + 1;
  return { startColumn };
}

